import {
  BUSINESS_PLAN_ID,
  LITE_PLAN_ID,
  PRO_PLAN_ID,
  WEEKLY_RECURRENCE
} from 'consts/user'
import { T } from 'consts/translationKeys'
import { useSelector } from 'react-redux'
import { ttsSelectors } from 'store/features/tts/selectors'
import { userSelectors } from 'store/features/user/selectors'
import { replaceTranslationVariables } from 'utils/text-transformation'
import { translate } from 'utils/translations'
import { useOpenUpgradePopup } from 'hooks/useOpenUpgradePopup'
import { REF_LINKS } from 'consts/refLinks'

export const useGetTtsGenerateActionPerLimit = (generateTts: () => void) => {
  const ttsLimitButtonAction = useOpenUpgradePopup()

  const isFreeUser = useSelector(userSelectors.isFreeUser)

  const isTtsCreditLimitTerminated = useSelector(
    ttsSelectors.isTtsCreditLimitTerminated
  )
  const isAnyPremiumVoiceSelectedSC = useSelector(
    ttsSelectors.isAnyPremiumVoiceSelected
  )
  const isPremiumVoiceSelected = isAnyPremiumVoiceSelectedSC && isFreeUser
  const isUpgradePopupNeeded =
    isTtsCreditLimitTerminated || isPremiumVoiceSelected

  const onClickAction = () =>
    isUpgradePopupNeeded
      ? ttsLimitButtonAction({ ref: REF_LINKS.V_EDITOR_TTS_GENERATE })
      : generateTts()

  return onClickAction
}

export const useGetTtsLimitDataPerSubscription = () => {
  const activeHighestSubscription = useSelector(
    userSelectors?.activeHighestSubscription
  )
  const { tariffId, recurrencePeriod } = activeHighestSubscription || {}

  const isWeeklyLiteSubscription =
    tariffId === LITE_PLAN_ID && recurrencePeriod === WEEKLY_RECURRENCE
  const isLiteSubscription =
    tariffId === LITE_PLAN_ID && recurrencePeriod !== WEEKLY_RECURRENCE
  const isProSubscription = tariffId === PRO_PLAN_ID
  const isBusinessSubscription = tariffId === BUSINESS_PLAN_ID

  const ttsLimitDataPerSubscription = [
    {
      condition: isWeeklyLiteSubscription,
      remainingLimitInfo: translate(T.WEEKLY_LITE_USER_TTS_LIMIT),
      expiringLimitWarning: translate(T.TTS_CREDIT_LIMIT_EXPIRING_WARNING),
      buttonInfo: translate(T.GET_MORE_MINUTES),
      isUpgradableSubscription: true
    },
    {
      condition: isLiteSubscription,
      remainingLimitInfo: translate(T.SUBSCRIBED_USER_TTS_CREDIT_LIMIT),
      expiringLimitWarning: translate(T.TTS_CREDIT_LIMIT_EXPIRING_WARNING),
      buttonInfo: translate(T.GET_MORE_MINUTES),
      isUpgradableSubscription: true
    },
    {
      condition: isProSubscription,
      remainingLimitInfo: translate(T.SUBSCRIBED_USER_TTS_CREDIT_LIMIT),
      expiringLimitWarning: translate(T.TTS_CREDIT_LIMIT_EXPIRING_WARNING),
      buttonInfo: translate(T.GET_MORE_MINUTES),
      isUpgradableSubscription: true
    },
    {
      condition: isBusinessSubscription,
      remainingLimitInfo: translate(T.SUBSCRIBED_USER_TTS_CREDIT_LIMIT),
      expiringLimitWarning: translate(T.TTS_CREDIT_LIMIT_EXPIRING_WARNING),
      buttonInfo: translate(T.CONTACT_SUPPORT),
      isUpgradableSubscription: true
    },
    {
      condition: true,
      remainingLimitInfo: translate(T.FREE_USER_TTS_CREDIT_LIMIT),
      expiringLimitWarning: translate(T.TTS_CREDIT_LIMIT_EXPIRING_WARNING),
      buttonInfo: translate(T.GET_MORE_MINUTES),
      isUpgradableSubscription: true
    }
  ]
  return ttsLimitDataPerSubscription.find(data => data.condition)
}

export const useGetUserTtsCreditLimitData = () => {
  const ttsLimitButtonAction = useOpenUpgradePopup()

  const creditLimitInMinutes = useSelector(ttsSelectors.ttsCreditLimitInMinutes)

  const isTtsCreditLimitExpiring = useSelector(
    ttsSelectors.isTtsCreditLimitExpiring
  )
  const isTtsCreditLimitTerminated = useSelector(
    ttsSelectors.isTtsCreditLimitTerminated
  )

  const isTtsCreditLimitAvailable = useSelector(
    ttsSelectors.isTtsCreditLimitAvailable
  )

  const currentData = useGetTtsLimitDataPerSubscription()

  const {
    remainingLimitInfo,
    expiringLimitWarning,
    buttonInfo,
    isUpgradableSubscription
  } = currentData || {}

  const ttsLimitMessagesPerCondition = [
    {
      condition: isTtsCreditLimitExpiring,
      message: expiringLimitWarning
    },
    {
      condition: isTtsCreditLimitAvailable,
      message: remainingLimitInfo
    },
    {
      condition: isTtsCreditLimitTerminated,
      message: remainingLimitInfo
    }
  ]

  const ttsLimitCurrentMessage =
    ttsLimitMessagesPerCondition.find(data => data.condition)?.message || ''

  const actionButtonMessage =
    isUpgradableSubscription || !isTtsCreditLimitAvailable ? buttonInfo : ''

  const ttsLimitCountMessage = isTtsCreditLimitTerminated
    ? `${creditLimitInMinutes}`
    : `~ ${creditLimitInMinutes}`

  const ttsLimitMessage =
    replaceTranslationVariables(ttsLimitCurrentMessage, [
      ttsLimitCountMessage
    ]) + ` <${actionButtonMessage}>`
  return {
    ttsLimitMessage,
    ttsLimitButtonAction,
    isUpgradableSubscription
  }
}
